import { Label } from './events.data';
import { EventListInfo } from './share.event-lists.data';
import { WFWorker, WorkerStatus } from './workforce.data';
import { Performer } from './performers.data';
import { Sponsor } from './sponsors.data';
import { Exhibitor } from './exhibitors.data';
import { Asset } from './assets.data';
import { JoiResource } from './resources.data';
import { SectionSettings } from './budget.data';
import { EventBackupBase } from './backup.data';
import { BrandingProps, FooterLinks, JoiBrandImage, JoiTemplate, SocialTemplate, createEventListBuilderData } from './publish.data';
import { makeDefaultLabels } from '../helpers/helpers';
import { Page } from './pages.data';
import { Attendee } from './attendees.data';

export type DocketStatus = 'active' | 'trial' | 'beta' | 'suspended' | 'incomplete' | 'fresh' | 'expired' | 'canceled' | 'custom' // lol what
export const activeDocketStatuses: DocketStatus[] = ['fresh','incomplete','trial','beta','custom', 'active']

export interface AccountIssueDisplayData {
  id: string;
  status: DocketStatus;
  days_remaining?: number;
}

export interface LineItemTemplate {
  id: string
  sortNumber: number
  name: string
  unitRate: number
  unitCost: number
}

export interface Docket {
  status: DocketStatus
  expiration?:any;
  limit?:number;
  tally?:number;
  methods?:any[];
  storage_plan?:string;
  stripeId?:string;
  stripePlanId?:string;
  subscription_plan?:string;
  subscription_id?:string;
  reciepts?:any;
  frequency?:number;
  interval?:string;
  currency?:string;
  incomplete_attempt_count?:number
  incomplete_next_payment_attempt?:number;
  // ux only properties
  days_remaining?:string;
  summary?:string;
  stripeSubscriptionId?: string
}

export interface SubscriptionPlanIds {
  quantity:number;
  stripePlanId:string;
}
export interface AccountUserInfo{
  uid?:string
  email:string
  firstName?:string
  lastName?:string
  photoURL?:string
  role?:string
  status?: WorkerStatus // this is necessary before untangling the assigning of a WFWorker to AccountUser =( Some ugly stuff there
}


export interface AccountUsers {
  managers:string[]
  userInfo:Partial<AccountUserInfo>[]
  users:string[]
}

export interface AccountUsersDBEntry {
  userInfo: AccountUserInfo[]
  managers: string[]
  users: string[]
}

export interface EntityClipboard {
  performers: { [performerId: string]: Performer }
  exhibitors: { [exhibitorId: string]: Exhibitor }
  sponsors: { [sponsorId: string]: Sponsor }
  assets: { [assetId: string]: Asset }
  resources: { [resourceId: string]: JoiResource }
  attendees: { [attendeeId: string]: Attendee }
}

export interface AccountClipboardPage extends EntityClipboard, Page {
  accountId: string
}

type BaseAccountPageKeys = Extract<keyof EntityClipboard, string>;
export const baseAccountPageKeys: BaseAccountPageKeys[] = [
  'performers',
  'exhibitors',
  'sponsors',
  'assets',
  'resources',
  'attendees'];

export const createNewAdminPageGenerator = (accountId: string) => (page: number): AccountClipboardPage => {
 const basePage: Partial<AccountClipboardPage> = { accountId, page, id: null } 
 baseAccountPageKeys.forEach(k => basePage[k] = {})
 return basePage as AccountClipboardPage
}

export const standardizeAdminPage = (adminPage: AccountClipboardPage): void => {
  baseAccountPageKeys.forEach(k => {
    if (!adminPage[k]) adminPage[k] = {}
  })
}


export interface TimestampDuck { // if it looks like a duck and quacks like a duck
  nanoseconds: number
  seconds: number
}

export interface AccountPublicInfo extends BrandingProps {
  headerId: string // making branding props required here
  footerId: string
  socialId: string
  templateId: string // making branding props required here
  linksId: string
  title: string
  id: string
  photoURL:string
  docket: { status: DocketStatus }
  is_suspended: boolean
  headers: JoiBrandImage[]
  footers: JoiBrandImage[]
  eventThumbs: JoiBrandImage[]
  templates: JoiTemplate[]
  socialTemplates: SocialTemplate[]
  footerLinks: FooterLinks[]

}

export interface Account extends Partial<AccountPublicInfo> {
  id:string;
  photoURL:string;
  title:string;
  settings: any;
  created:{ creator:string, creatorUid:string, date: TimestampDuck }
  currency:string;
  country:string;
  docket:Docket;
  invitation: AccountInvitationRecord[];
  owner:string;
  labels:Label[]
  lastActive:Date;
  solo:string[]
  executive:string[]
  leader:string[]
  admin:string[]
  users:string[]
  userInfo:Partial<AccountUserInfo>[]
  updatedAt:Date
  customPublishCSSUrl?: string;
  // ux only properties
  is_admin?:boolean
  is_exec?:boolean
  is_solo?:boolean
  is_leader?:boolean
  is_manager?:boolean
  is_suspended?:boolean
  is_invited?:boolean
  invited_as_role?:string
  invite_sender?:WFWorker
  labels_obj:any
  eventListInfos?: { [id:string]: EventListInfo }
  lineItems: LineItemTemplate[]
  clipboard: EntityClipboard
  // budget section settings defaults.  Same property keys / names as SectionSettings
  taxRate?: number
  discountRate?: number
  feeRate?: number
  taxInclusive?: boolean
  markupRate?: number
  eventBackups?: { [eventId:string]: EventBackupBase[] }
  subscription?: Docket
}


export const accountBudgetRateKeys: (keyof Account & keyof SectionSettings)[] = ['feeRate', 'taxRate', 'discountRate', 'taxInclusive', 'markupRate']

export interface HeaderImage {
  id: string
  name: string
  url: string
  thumbUrl: string
}

export interface FooterText {
  id: string
  name: string // being lazy...
}


export interface AccountInvitationRecord {
  receiver:string
  status:string
  role?:string
  sender:string
}

/* tslint:disable */
export interface JoiAccount extends Account {} // every auto import of account fails
/* tslint:enable */


export const DBAccountPropertyWhiteList: Array<keyof JoiAccount> = [
  'id',
  'photoURL',
  'lineItems',
  'title',
  'settings',
  'created',
  'currency',
  'country',
  'docket',
  'invitation',
  'owner',
  'labels',
  'lastActive',
  'admin',
  'executive',
  'leader',
  'solo',
  'users',
  'updatedAt',
  'eventListInfos',
  'headers',
  'headerId',
  'footers',
  'footerId',
  'eventThumbs',
  'feeRate',
  'taxRate',
  'discountRate',
  'taxInclusive',
  'markupRate',
  'eventBackups',
  'templateId',
  'templates',
  'linksId',
  'socialTemplates',
  'socialId',
  'footerLinksId',
  'footerLinks'
]

export interface Promocode {
  code?:string;
  amount_off?:number;
  percent_off?:number;
  name?:string;
}


export const isAccountManager = (account: Partial<Account>, uid: string) => {
  const isManager = account && uid && (account.admin.includes(uid) || account.executive.includes(uid))
  return isManager
}
  
export const isAccountAdmin = (account: Partial<Account>, uid: string) => {
  // console.log({ account })
  const isAdmin = account && uid && account.admin?.includes(uid)
  return isAdmin
}

export const RoleNames = { admin: 'Administrator', executive: 'Team Leader', leader: 'Team Member', solo: 'Team Contributor' }
  
export const standardizeAccountClipboardPage = (page: AccountClipboardPage) => { // mutates in place and returns
  baseAccountPageKeys.filter(k => !page[k]).forEach(k => page[k] = {})
  return page
}

export const standardizeAccount = (account: JoiAccount, uid?: string): void => { // mutates in place
  // console.log({ account });
  if (!account || !account.users) return;
  // console.log({ user_list});
  // add any user specific info to account data here

  if (uid) {
    account.is_admin = account.admin.includes(uid);
    account.is_exec = account.executive.includes(uid);
    account.is_solo = account.solo.includes(uid);
    account.is_leader = account.leader.includes(uid)
    account.is_manager = isAccountManager(account, uid)
  }

  account.is_invited = false;
  // console.log({ id:account.id, user_list:user_list[account.id] });
  // add role and sender data if available

  if(!account.labels) account.labels = makeDefaultLabels()
  if(!account.eventListInfos) account.eventListInfos = {}
  if (!account.lineItems) account.lineItems = []

  if (account.invitation && uid){
    const invited_data = account.invitation
      .filter(inv => inv.receiver === uid && inv.status === 'pending')
      .map(inv=>({role: RoleNames[inv.role], sender:inv.sender}))
    if(invited_data.length > 0){
      // console.log('invited_data ', invited_data);
      account.invited_as_role = invited_data[0].role;
      if(account.userInfo) {
        account.invite_sender = account.userInfo.find(user=>invited_data[0].sender === user.uid);
      }else
      account.is_invited = true;
    }
  }
  cleanClipboard(account)
  if (!account.templates) account.templates = []
  if (!account.headers) account.headers = []
  if (!account.footers) account.footers = []
  if (!account.eventThumbs) account.eventThumbs = []
  if (!account.socialTemplates) account.socialTemplates = []
  if (!account.footerLinks) account.footerLinks = []
  Object.values(account.eventListInfos).filter(eli => !eli.homePageData).forEach(eli => eli.homePageData = createEventListBuilderData()) // mutates in place
  Object.values(account.eventListInfos).filter(eli => !eli.instanceThumbs).forEach(eli => eli.instanceThumbs = {})
}

export const cleanClipboard = <T extends Partial<Account>>(passedAccount: T) => {
  const account = { ...passedAccount } as Partial<Account>
  account.clipboard = {} as EntityClipboard
  baseAccountPageKeys.forEach(k => account.clipboard[k] = {})
  return account;
}

export const combineAccountAndClipboardPages = (account: Partial<Account>, clipboardPages: AccountClipboardPage[]): Partial<Account> => { // mutates in place and returns account
  if (!account) return null

  account = cleanClipboard(account)
  if (!clipboardPages?.length) return account
  console.log({ account, clipboardPages })
  baseAccountPageKeys.forEach(k => {
    if (!account.clipboard[k]) account.clipboard[k] = {}
    clipboardPages.forEach(page => {
      if (page[k]) {
        Object.keys(page[k]).forEach(id => {
          if (!account.clipboard[k][id]) account.clipboard[k][id] = page[k][id]
        })
      }
    })
  })
  account.clipboard = { ...account.clipboard } // for clipboard reference
  return account
}

export const getPublicAccountFromAccount = <D extends { status: DocketStatus }>(account: Partial<JoiAccount>, docket: D): AccountPublicInfo => {
  const publicAccount: AccountPublicInfo = {
    id: account.id,
    docket: { status: docket.status },
    title: account.title || '',
    headerId: account.headerId || null,
    footerId: account.footerId || null,
    socialId: account.socialId || null,
    templateId: account.templateId || null,
    linksId: account.linksId || null,
    photoURL: account.photoURL || null,
    is_suspended: account.is_suspended || null,
    headers: account.headers || [],
    footers: account.footers || [],
    eventThumbs: account.eventThumbs || [],
    templates: account.templates || [],
    socialTemplates: account.socialTemplates || [],
    footerLinks: account.footerLinks || []
  }
  // console.log({ account, publicAccount })
  return publicAccount
}
